import Styles from './CalendarEventCard.module.scss'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { CSSProperties, forwardRef, ReactNode } from 'react'
import {
  TYPES_WITH_CAPTION_ABOVE_IMAGE,
  TYPES_WITH_CAPTION_BELOW_IMAGE,
  TYPES_WITH_PREVIEW,
} from 'screens/Calendar/FullScreenPreview/Types'
import { TruncatedText } from './TruncatedText'
import cx from 'classnames'
import { Duration } from './Duration'
import { isArticle } from 'screens/ScheduledPosts/utils'

interface ImageWrapperProps {
  event: AlmanacFileCalendarEvent
  loadingColor: string
  children: ReactNode
  cardWidth: number
  showCaption?: boolean | undefined
  isLoading?: boolean | undefined
  allowExpand?: boolean
}

export const ImageWrapper = forwardRef<HTMLDivElement, ImageWrapperProps>(
  ({ event, loadingColor, children, cardWidth, showCaption, isLoading, allowExpand = true }, fRef) => {
    const {
      width,
      height,
      channel,
      content,
      key,
      duration,
      scheduled_post,
      scheduled_post_targets,
      scheduled_post_provider,
      external_integration_id: primaryIntegrationId,
    } = event
    const primaryTarget = scheduled_post_targets?.find(
      (t) => t.provider === scheduled_post_provider && t.external_integration_id === primaryIntegrationId,
    )
    const caption = primaryTarget?.target_overrides?.content || scheduled_post?.content || content

    const showPostPreview = channel && TYPES_WITH_PREVIEW.includes(channel)

    const isArticlePreview = !showPostPreview && isArticle({ almanac_file: event })
    const aspectRatioWidth = isArticlePreview ? 118 : width || 1
    const aspectRatioHeight = isArticlePreview ? 172 : height || 1

    const hasCaptionAboveImage = channel && TYPES_WITH_CAPTION_ABOVE_IMAGE.includes(channel)
    const hasCaptionBelowImage = channel && TYPES_WITH_CAPTION_BELOW_IMAGE.includes(channel)

    const captionTopOffset = hasCaptionAboveImage ? 41 : 0
    const captionBottomOffset = hasCaptionBelowImage && !hasCaptionAboveImage ? 49 : 0

    const styles = {
      '--brand-kit-color': loadingColor,
      '--aspect-w': `${aspectRatioWidth}`,
      '--aspect-h': `${aspectRatioHeight}`,
      '--caption-top-offset': `${captionTopOffset}px`,
      '--caption-bottom-offset': `${captionBottomOffset}px`,
    } as CSSProperties

    return (
      <>
        {showCaption && hasCaptionAboveImage && (
          <div className={cx(Styles.captionContainer, Styles.textAboveImage)}>
            <TruncatedText text={caption || ''} cardWidth={cardWidth} allowExpand={allowExpand} />
          </div>
        )}
        <div
          className={cx(Styles.eventImage, { [Styles.isLoading]: isLoading })}
          style={styles}
          id={`image-wrapper-${key}`}
          ref={fRef}
        >
          {children}
          {!isLoading && <Duration duration={duration ?? undefined} />}
        </div>
        {showCaption && hasCaptionBelowImage && (
          <div className={Styles.captionContainer}>
            <TruncatedText text={caption || ''} cardWidth={cardWidth} allowExpand={allowExpand} />
          </div>
        )}
      </>
    )
  },
)
